import { useState } from 'react';
import { useTranslations } from '@vp/om-translations';
import { Button, FlexBox, Span, Spinner, VisuallyHidden } from '@vp/swan';
import { reorderItemsWithSpecifiedQuantities } from '../../../apis/oreosApi';
import { useLineItemReorderActionStatus } from '../../contexts/LineItemReorderActionStatusContext';
import { useSourcePageContext } from '../../contexts/SourcePageContext';
import { useLineItemData } from '../../contexts/LineItemDataContext';
import { useShopperId } from '../../../hooks/useShopperId';
import { useSiteData } from '../../../hooks/useSiteData';
import { BUY_IT_AGAIN_SOURCE_PAGE } from '../../../constants/sourcePage';
import type { ReorderResponse } from '../../../types/reorder.types';
import {
  IS_REORDERABLE_FALSE,
  REORDER_ACTION,
} from '../../../constants/newRelic';
import {
  trackDocumentConversionRemediationPresentedToCustomer,
  trackReorderUnavailableForProduct,
} from '../../../apis/newrelic/newRelicTrackingApi';
import {
  isDocumentConversionFailure,
  isProductUnavailableForReorder,
} from '../../../utils/reorder';
import {
  docRemediationModalPresentedToCustomer,
  trackBuyItAgainReorderProductAddedToCart,
  trackReorderProductAddedToCart,
} from '../../../apis/trackingApi';
import { invalidateMiniCartData } from '../../../utils/addToCart';
import type { PricedQuantity } from '../../../types/ReorderableItems.types';
import { useReorderQuantity } from '../../../hooks/useReorderQuantity';
import { useFlawedAddToCartTracker } from '../../../hooks/useFlawedAddToCartTracker';

interface ItemAddToCartButtonProps {
  buttonSkin?: 'link' | 'primary' | 'secondary';
  buttonWidth: 'full-width' | 'standard';
}

export const ItemAddToCartButton = (props: ItemAddToCartButtonProps) => {
  const { buttonSkin, buttonWidth } = props;
  const [orderHistoryMessages = {}, sharedActionMessages = {}] =
    useTranslations(['orderHistoryMessages', 'sharedActionMessages']);

  const item = useLineItemData();
  const {
    id,
    actions: { reorder: isReorderable },
    orderNumber,
    productName,
  } = item;

  const shopperId = useShopperId();

  const {
    setAddToCartFailure,
    setAddToCartSuccess,
    setDocConversionFailure,
    setAltDocId,
    setProductUnavailableFailure,
    resetReorderActionFeedback,
  } = useLineItemReorderActionStatus();

  const { sourcePage } = useSourcePageContext();
  const { tenantEnvironment, vistacartTenant: tenant } = useSiteData();

  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);
  const { quantity: itemQuantityForReorder, selectedPricedQuantity } =
    useReorderQuantity();

  const trackAddToCartError = useFlawedAddToCartTracker();

  if (!id || id === '') {
    return null;
  }

  const handleProductUnavailable = (
    reorderResponse: ReorderResponse | null
  ) => {
    setProductUnavailableFailure(true);

    const failureReason =
      reorderResponse?.results[0]?.failureReason || IS_REORDERABLE_FALSE;
    const analyticsMetadata = {
      sourcePage,
      shopperId,
      orderNumber,
      lineItemId: item.id,
      productKey: item.productKey,
      productVersion: item.productVersion,
      tenant,
      action: REORDER_ACTION,
      failureReason,
    };
    trackReorderUnavailableForProduct(analyticsMetadata);
  };

  const handleDocumentConversionFailure = (
    reorderResponse: ReorderResponse
  ) => {
    const failedResult = reorderResponse?.results[0];
    // there was a doc conversion failure, lets pop that modal!
    setDocConversionFailure(true);
    setAltDocId(failedResult?.altDocId || '');

    const analyticsMetadata = {
      sourcePage,
      orderNumber,
      tenant,
      lineItemId: id,
      action: REORDER_ACTION,
    };
    docRemediationModalPresentedToCustomer(analyticsMetadata);
    trackDocumentConversionRemediationPresentedToCustomer(analyticsMetadata);
  };

  /**
   * Given a response from the reorder service, determine if that error was due to a document conversion failure or product unavailable failure
   * @param reorderResponse
   * @returns {Promise<void>}
   */
  const handleErrorResponse = async (
    reorderResponse: ReorderResponse | Error
  ) => {
    if (reorderResponse instanceof Error) {
      setAddToCartFailure(true);
      return;
    }

    // does the response contain a lineItem result that failed due to doc issues?
    if (isDocumentConversionFailure(reorderResponse)) {
      handleDocumentConversionFailure(reorderResponse);
    } else if (isProductUnavailableForReorder(reorderResponse)) {
      handleProductUnavailable(reorderResponse);
    } else {
      setAddToCartFailure(true);
    }
  };

  const addToCartOnClickHandler = async () => {
    resetReorderActionFeedback(REORDER_ACTION);

    if (isReorderable) {
      setAwaitingApiResponse(true);
      try {
        const reorderItems = [
          {
            lineItemId: id,
            quantity: itemQuantityForReorder || 0,
            analytics: {
              source: sourcePage,
            },
          },
        ];

        await reorderItemsWithSpecifiedQuantities({
          orderNumber,
          reorderItems,
          reorderSourcePage: sourcePage,
          shopperId,
          tenantEnvironment,
          vistacartTenantName: tenant,
        });

        setAwaitingApiResponse(false);
        setAddToCartSuccess(true);
        invalidateMiniCartData();

        // track reorder being added to cart successfully
        // selectedPriceQuantity should be defined when source page is BUY_IT_AGAIN_SOURCE_PAGE but adding in an extra check for ts
        if (
          sourcePage === BUY_IT_AGAIN_SOURCE_PAGE &&
          !!selectedPricedQuantity
        ) {
          trackBuyItAgainReorderProductAddedToCart({
            item,
            pricedQuantity: selectedPricedQuantity as PricedQuantity, // will not be null on BIA page
          });
        } else {
          trackReorderProductAddedToCart(item, sourcePage);
        }
      } catch (err) {
        setAwaitingApiResponse(false);
        trackAddToCartError(err);
        await handleErrorResponse(err as Error);
      }
    } else {
      handleProductUnavailable(null);
    }
  };

  return (
    <Button
      onClick={addToCartOnClickHandler}
      disabled={awaitingApiResponse}
      width={buttonWidth}
      skin={buttonSkin}
    >
      {awaitingApiResponse && (
        <FlexBox justifyContent="center">
          <Spinner
            accessibleText={sharedActionMessages?.['loading']}
            aria-busy="true"
            aria-live="polite"
            role="status"
            size="standard"
          />
        </FlexBox>
      )}
      {/* This padding prevents the button from resizing when the preloader appears/disappears */}
      <Span mx={awaitingApiResponse ? 0 : 3}>
        {orderHistoryMessages.addToCartButtonText}
      </Span>
      <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
    </Button>
  );
};
